import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight, Crown, Medal, Swords } from "lucide-react";

import { api } from "@/lib/api";

const initialsFor = (name) => (name || "").trim().split(/\s+/).map((part) => part[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

function ChampionPhotos({ photos = [], fallback, name }) {
  const list = (photos || []).length ? photos : (fallback ? [{ data_base64: fallback, main: true }] : []);
  const sorted = [...list].sort((a, b) => (b.main ? 1 : 0) - (a.main ? 1 : 0));
  const [idx, setIdx] = useState(0);
  const [touch, setTouch] = useState(null);
  if (!sorted.length) return null;
  const prev = () => setIdx((i) => (i - 1 + sorted.length) % sorted.length);
  const next = () => setIdx((i) => (i + 1) % sorted.length);
  const onTouchStart = (e) => setTouch(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touch == null) return;
    const diff = e.changedTouches[0].clientX - touch;
    if (Math.abs(diff) > 40) diff < 0 ? next() : prev();
    setTouch(null);
  };
  return <div data-testid="champion-slider" style={{position:"relative",display:"inline-block"}} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
    <img src={sorted[idx].data_base64} alt={name || "Champion"} data-testid="champion-photo" />
    {sorted.length > 1 && <>
      <button type="button" onClick={prev} data-testid="champion-slider-prev" aria-label="Zurück" style={{position:"absolute",left:6,top:"50%",transform:"translateY(-50%)",background:"rgba(0,0,0,.55)",border:"none",color:"#f4d33e",padding:6,borderRadius:"50%",cursor:"pointer"}}><ChevronLeft size={20} /></button>
      <button type="button" onClick={next} data-testid="champion-slider-next" aria-label="Weiter" style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",background:"rgba(0,0,0,.55)",border:"none",color:"#f4d33e",padding:6,borderRadius:"50%",cursor:"pointer"}}><ChevronRight size={20} /></button>
      <div style={{position:"absolute",bottom:6,left:0,right:0,textAlign:"center"}} data-testid="champion-slider-dots">{sorted.map((_, i) => <span key={i} style={{display:"inline-block",width:8,height:8,borderRadius:"50%",margin:"0 3px",background:i===idx?"#f4d33e":"rgba(255,255,255,.4)"}} />)}</div>
    </>}
  </div>;
}

export default function RankingsPage() {
  const [records, setRecords] = useState([]);
  const [weightClasses, setWeightClasses] = useState([]);
  // Rangliste startet standardmäßig auf Profi + Männlich. Diese Vorbelegung gilt
  // ausschließlich für diese Ansicht und beeinflusst keine Event-Einstellungen.
  const [division, setDivision] = useState("pro");
  const [gender, setGender] = useState("male");
  const [selectedWeight, setSelectedWeight] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/rankings"), api.get("/settings/weight-classes")]).then(([rankingResponse, classResponse]) => {
      setRecords(rankingResponse.data);
      setWeightClasses(classResponse.data);
    });
  }, []);

  const availableWeights = useMemo(
    () => weightClasses
      .filter((item) => item.gender === gender && item.division_type === division)
      .sort((a, b) => {
        const upper = (item) => item.max_weight ?? Infinity;
        return upper(b) - upper(a);
      }),
    [weightClasses, gender, division],
  );

  const recordFor = (name) => records.find((record) => record.gender === gender && (record.division_type || "pro") === division && record.weight_class === name);

  const changeDivision = (nextDivision) => { setDivision(nextDivision); setSelectedWeight(null); };
  const changeGender = (event) => { setGender(event.target.value); setSelectedWeight(null); };

  if (selectedWeight) {
    const record = recordFor(selectedWeight);
    return (
      <div className="content-frame" data-testid="rankings-page">
        <header className="page-heading">
          <div>
            <p className="eyebrow">WBC GERMANY</p>
            <h1>{selectedWeight}</h1>
            <p className="muted">{division === "amateur" ? "Amateur" : "Profi"} · {gender === "male" ? "Herren" : "Damen"}</p>
          </div>
          <button className="button ghost" onClick={() => setSelectedWeight(null)} data-testid="ranking-back-button"><ArrowLeft size={17} /> Zurück</button>
        </header>
        {record?.champion_name || record?.champion_photo || (record?.photos||[]).length ? (
          <section className="champion-hero" data-testid="ranking-champion-hero">
            <ChampionPhotos photos={record.photos} fallback={record.champion_photo} name={record.champion_name} />
            <div>
              <span className="crown-badge"><Crown size={17} /> CHAMPION</span>
              <p>{record.weight_class}</p>
              <h2>{record.champion_name || "—"}</h2>
              {record.champion_club && <span>{record.champion_club}</span>}
              {(record.titles||[]).length > 0 && <div data-testid="champion-titles" style={{marginTop:10,color:"#f4d33e"}}>
                <b style={{fontSize:11,letterSpacing:0.6,display:"block",marginBottom:4}}>TITEL</b>
                <ul style={{margin:0,paddingLeft:16,fontSize:12,color:"#d4c88e"}}>
                  {record.titles.map((t,i) => <li key={i} data-testid={`champion-title-${i}`}>{t.label}{t.date ? ` · ${t.date}` : ""}</li>)}
                </ul>
              </div>}
            </div>
          </section>
        ) : (
          <div className="champion-pending" data-testid="ranking-champion-pending">Champion wird in Kürze eingetragen</div>
        )}
        <section className="rankings-grid">
          <article className="ranking-card" data-testid="ranking-card-detail">
            <header>
              <span>{gender === "male" ? "HERREN" : "DAMEN"} · {selectedWeight}</span>
              <h2><Swords size={16} /> CONTENDER</h2>
            </header>
            {(() => {
              const filled = (record?.entries || []).filter((entry) => entry.fighter_name?.trim());
              if (!filled.length) return <p className="ranking-empty-note">Noch keine Contender platziert.</p>;
              return filled.map((entry) => (
                <div className={`ranking-row rank-${entry.rank}`} key={entry.rank} data-testid={`ranking-entry-detail-${entry.rank}`}>
                  <b>#{entry.rank}</b>
                  <div>
                    <strong>{entry.fighter_name}</strong>
                    <small>{entry.club}</small>
                  </div>
                  <span>{entry.points} Pkt.</span>
                </div>
              ));
            })()}
          </article>
        </section>
      </div>
    );
  }

  return (
    <div className="content-frame" data-testid="rankings-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">WBC GERMANY</p>
          <h1>RANGLISTE</h1>
          <p className="muted">Champion pro Gewichtsklasse. Klicke eine Kachel für die vollständige Contender-Liste.</p>
        </div>
        <div className="ranking-selects">
          <div className="division-toggle" role="tablist" aria-label="Turnier-Typ" data-testid="ranking-division-toggle">
            <button type="button" role="tab" aria-selected={division === "amateur"} className={`division-tab ${division === "amateur" ? "active" : ""}`} onClick={() => changeDivision("amateur")} data-testid="ranking-division-amateur">AMATEUR</button>
            <button type="button" role="tab" aria-selected={division === "pro"} className={`division-tab ${division === "pro" ? "active" : ""}`} onClick={() => changeDivision("pro")} data-testid="ranking-division-pro">PROFI</button>
          </div>
          <label>Geschlecht
            <select value={gender} onChange={changeGender} data-testid="ranking-gender-select">
              <option value="male">Männlich</option>
              <option value="female">Weiblich</option>
            </select>
          </label>
        </div>
      </header>
      {availableWeights.length ? (
        <section className="champion-tile-grid" data-testid="champion-tile-grid">
          {availableWeights.map((weightClass) => {
            const record = recordFor(weightClass.name);
            const hasChampion = !!(record?.champion_name || record?.champion_photo);
            return (
              <button
                key={weightClass.id}
                type="button"
                className={`champion-tile ${hasChampion ? "filled" : "pending"}`}
                onClick={() => setSelectedWeight(weightClass.name)}
                data-testid={`champion-tile-${weightClass.name}`}
              >
                <span className="tile-weight-class">{weightClass.name}</span>
                <span className="crown-badge tile-badge"><Crown size={13} /> CHAMPION</span>
                {hasChampion ? (
                  <>
                    <div className="tile-avatar" aria-hidden="true">
                      {record.champion_photo ? <img src={record.champion_photo} alt={record.champion_name || "Champion"} /> : initialsFor(record.champion_name)}
                    </div>
                    <b className="tile-name" data-testid={`champion-tile-name-${weightClass.name}`}>{record.champion_name || "—"}</b>
                    <small className="tile-club">{record.champion_club || ""}</small>
                  </>
                ) : (
                  <>
                    <div className="tile-avatar placeholder" aria-hidden="true">?</div>
                    <b className="tile-name muted">Noch keine Eintragung</b>
                  </>
                )}
              </button>
            );
          })}
        </section>
      ) : (
        <div className="empty-state" data-testid="rankings-empty-state">
          <Medal size={32} />
          <h2>KEINE GEWICHTSKLASSEN GEFUNDEN</h2>
          <p>Für diese Kombination sind aktuell keine Klassen hinterlegt.</p>
        </div>
      )}
    </div>
  );
}
